function toDate(input) {
  if (input instanceof Date) return input
  return new Date(input)
}

const SV_LOCALE = 'sv-SE'
const TZ = 'Europe/Stockholm'

function formatYMDHM(date) {
  const parts = new Intl.DateTimeFormat(SV_LOCALE, {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = (type) => parts.find((p) => p.type === type)?.value ?? ''

  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`
}

function formatYMD(date) {
  const parts = new Intl.DateTimeFormat(SV_LOCALE, {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date)
  const get = (type) => parts.find((p) => p.type === type)?.value ?? ''

  return `${get('year')}-${get('month')}-${get('day')}`
}

export const readableDate = function (date, dateonly = false) {
  const d = toDate(date)
  if (Number.isNaN(d.getTime())) return ''
  return dateonly ? formatYMD(d) : formatYMDHM(d)
}

export const duedateClasses = function (duedate) {
  const redClasses = 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
  const yellowClasses =
    'bg-amber-100 text-amber-800 dark:bg-amber-800 dark:text-amber-100'

  const d = toDate(duedate)
  if (Number.isNaN(d.getTime())) return ''

  const diffInDays = Math.floor((d.getTime() - Date.now()) / 86400000)

  if (diffInDays < 0) {
    return redClasses
  }

  if (diffInDays < 2) {
    return yellowClasses
  }

  return ''
}

export const duedatebackgroundClasses = function (duedate, modules) {
  const defaultClasses = 'dark:hover:bg-gray-600'
  if (!duedate || !modules?.duedate_background || !modules?.duedate) {
    return defaultClasses
  }

  const redClasses =
    'bg-red-50 hover:bg-red-100 dark:bg-red-900 dark:hover:bg-red-800'
  const yellowClasses =
    'bg-amber-50 hover:bg-amber-100 dark:bg-amber-900 dark:hover:bg-amber-800'

  const d = toDate(duedate)
  if (Number.isNaN(d.getTime())) return defaultClasses

  const diffInDays = Math.floor((d.getTime() - Date.now()) / 86400000)

  if (diffInDays < 0) {
    return redClasses
  }

  if (diffInDays < 2) {
    return yellowClasses
  }

  return defaultClasses
}

export const t = function (en, sv) {
  return window.language === 'en' ? en : sv
}

/**
 * Spells out the range a ticket template's date field accepts.
 *
 * The browser refuses dates outside min/max but gives no reason for it, which
 * leaves the user with a greyed out calendar and no explanation.
 *
 * @param {{ min: ?string, max: ?string }} dateConfig Resolved bounds from the backend
 * @returns {string} Empty when the field takes any date
 */
export const dateFieldHint = function (dateConfig) {
  if (!dateConfig) {
    return ''
  }

  // The bounds are whole days even when the field carries a time, so the time is dropped
  const min = dateConfig.min ? dateConfig.min.slice(0, 10) : null
  const max = dateConfig.max ? dateConfig.max.slice(0, 10) : null

  if (min && max) {
    return t(
      `Choose a date between ${min} and ${max}`,
      `Välj ett datum mellan ${min} och ${max}`,
    )
  }

  if (min) {
    return t(`Choose ${min} or later`, `Välj ${min} eller senare`)
  }

  if (max) {
    return t(`Choose ${max} at the latest`, `Välj senast ${max}`)
  }

  return ''
}

/**
 * Spells out what a ticket template's file field accepts.
 *
 * The `accept` attribute only filters the file picker and does nothing at all for a
 * dragged file, so the rule has to be written out for the user as well.
 *
 * @param {{ extensions: string[], max_bytes: number }} fileConfig Resolved config from the backend
 * @returns {string} Empty when the field has no configuration
 */
export const fileFieldHint = function (fileConfig) {
  if (!fileConfig) {
    return ''
  }

  const megabytes = Math.round(fileConfig.max_bytes / 1024 / 1024)
  const size = t(`max ${megabytes} MB`, `max ${megabytes} MB`)

  if (!fileConfig.extensions || fileConfig.extensions.length === 0) {
    return t(`Any file type, ${size}`, `Valfri filtyp, ${size}`)
  }

  const types = fileConfig.extensions.map((ext) => '.' + ext).join(', ')

  return t(`Allowed: ${types}, ${size}`, `Tillåtet: ${types}, ${size}`)
}

/**
 * Build the avatar image markup for a ticket owner.
 *
 * Falls back to gravatar for users without an uploaded avatar.
 *
 * @param {{ id: number, user: string, md5mail: string, mail: string }} owner
 * @param {{ id: number }[]} avatars Users known to have an uploaded avatar
 */
export const getAvatar = function (owner, avatars = []) {
  const src = avatars.some((row) => row.id === owner.id)
    ? '/user/' + owner.id + '/avatar'
    : 'https://www.gravatar.com/avatar/' + owner.md5mail + '?r=g&d=mp&s=70'

  return `<img alt="User avatar" class="rounded-full object-contain w-[35px] h-[35px]" title="${owner.user} (${owner.mail})" src="${src}">`
}
